import uniqBy from 'lodash/uniqBy';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';

export const DEFAULT_SIZE_PAGE = 10;
export const DEFAULT_PAGE = 1;

const STATUS_AVAILABLE = 'AVAILABLE';
const STATUS_UNAVAILABLE = 'UNAVAILABLE';

export const statusType = {
  STATUS_AVAILABLE,
  STATUS_UNAVAILABLE,
};

export function getFilteredProductsByText(products, sorting) {
  const productSearchValue = {};
  products?.forEach((product) => {
    const searchValues = new Set();
    // Add product name
    searchValues.add(product.name.toLowerCase());

    // add modifier names
    product.modifierGroups.forEach((modifierGroupContainer) => {
      searchValues.add(modifierGroupContainer.modifierGroup.name.toLowerCase());
    });

    productSearchValue[product.uuid] = searchValues;
  });

  const productsList = [];
  products?.forEach((product) => {
    const finalSearchValue = sorting?.filterByText.toLowerCase();
    const productSearchTermSet = productSearchValue[product.uuid];

    if (!finalSearchValue) {
      productsList.push(product);
      return;
    }
    const searchTerms = Array.from(productSearchTermSet);
    for (let i = 0; i < productSearchTermSet.size; i += 1) {
      const term = searchTerms[i];
      if (term.includes(finalSearchValue)) {
        productsList.push(product);
        return;
      }
    }
  });
  return productsList;
}

export function getFilteredProductsByCategories(products, categoriesByUuid) {
  const productList = [];
  products?.forEach((product) => {
    product?.categories?.forEach((categoryContainer) => {
      if (categoriesByUuid?.includes(categoryContainer?.category?.uuid)) {
        productList.push(product);
      }
    });
  });
  return uniqBy(productList, 'uuid');
}

export const pieceUnits = 'PIECES';
export const gramUnits = 'GRAMS';
export const kilogramUnits = 'KILOGRAM';
export const litreUnits = 'LITRE';
export const millilitreUnits = 'MILLILITRE';
export const units = [pieceUnits, gramUnits, kilogramUnits, litreUnits, millilitreUnits];

const PROPERTY_NAME = 'name';

const DESC = 'desc';
const ASC = 'asc';

export const sortingOrderTypes = {
  DESC,
  ASC,
};

export const sortingPropertyTypes = {
  NAME: PROPERTY_NAME,
};

const FILTER_PROPERTIES = [PROPERTY_NAME];

export function getFilteredMenuProductsByText(products, sorting) {
  return products.filter(
    (product) =>
      FILTER_PROPERTIES.filter((property) =>
        product[property].toLowerCase().includes(sorting.filterByText.toLowerCase()),
      ).length,
  );
}

export const filtersTableProductsTypes = {
  all: 'all',
  articles: 'articles',
  soldAlone: 'soldAlone',
};

export const optionsFilter = [
  { label: 'Ver todo', value: filtersTableProductsTypes.all },
  { label: 'Artículos', value: filtersTableProductsTypes.articles },
  { label: 'Personalizables', value: filtersTableProductsTypes.soldAlone },
];

const GENERAL = 'GENERAL';
const ADVANCED = 'ADVANCED';

export const tabsFormOptions = {
  GENERAL,
  ADVANCED,
};

export const tabsFormProduct = [
  {
    value: GENERAL,
    label: 'menuMaker:productForm.tabs.general',
    testId: 'product-form-tab-general',
  },
  {
    value: ADVANCED,
    label: 'menuMaker:productForm.tabs.advanced',
    testId: 'product-form-tab-advanced',
  },
];

export const sortByToLower = (option) => {
  return option?.name?.toLowerCase();
};

export function formatAmount(amount) {
  return amount?.replace('$', '');
}

export const defaultProduct = {
  nameProductField: '',
  descriptionProductField: '',
  imageProductField: '',
  soldAloneField: 1,
  priceProductField: '',
  selectedCategories: [],
  selectedModifierGroups: [],
  skuFieldMultiStore: '',
  posNameField: '',
  taxServingUnitField: '',
  taxProductKeyField: '',
  // Paired with taxProductKeyField. Holds the description resolved against Facturama (sent
  // by the backend on detail reads). Used purely for display in the autocomplete.
  taxProductKeyDescriptionField: '',
  kitchenAreasFields: [],
  exemptField: false,
  taxGroupFieldIVA: '',
  taxGroupFieldIEPS: '',
  canSelectOtherAreaField: false,
  kitchenAreasFieldsCanPrint: false,
  colorField: 'FFFFFF',
  barcodeField: '',
};

export const defaultProductObject = {
  serving_unit: 'PIECES',
  pos_name: '',
  sku: '',
  kitchen_areas: {},
  is_exempt: false,
  taxes: [],
};

export function getInitialAutocompleteProduct(optionsSelectedProduct, availableOptions, type, includeOverride = false) {
  const initialOptions = [];
  optionsSelectedProduct?.forEach((group) => {
    availableOptions?.forEach((option) => {
      const groupUuid = group[type]?.uuid ? group[type]?.uuid : group[type];

      if (option.uuid === groupUuid) {
        const itemOption = { ...option };

        if (includeOverride) {
          itemOption.hasOverride = group[type]?.hasOverride ?? group?.hasOverride;
        }
        initialOptions.push(itemOption);
      }
    });
  });

  return initialOptions;
}

export function productFormIsDirty(initProduct, productForm) {
  const selectedCategoriesTmp = productForm?.selectedCategories ?? [];
  const selectedModifierGroupsTmp = productForm?.selectedModifierGroups ?? [];
  const initProductsTmp = { ...initProduct, kitchenAreasFields: initProduct?.kitchenAreasFields?.sort() };
  let productFormTmp = {
    ...productForm,
    selectedCategories: selectedCategoriesTmp,
    selectedModifierGroups: selectedModifierGroupsTmp,
    priceProductField: formatAmount(productForm?.priceProductField),
    kitchenAreasFields: productForm?.kitchenAreasFields?.sort(),
  };

  if (productFormTmp?.exemptField) {
    delete productFormTmp?.taxGroupFieldIVA;
    delete productFormTmp?.taxGroupFieldIEPS;
    delete initProductsTmp?.taxGroupFieldIVA;
    delete initProductsTmp?.taxGroupFieldIEPS;
  }

  if (productFormTmp?.soldAloneField !== 2) {
    delete initProduct?.serving;
    delete initProduct?.servingUnit;
  } else {
    productFormTmp = { ...productFormTmp, serving: Number(productFormTmp?.serving) };
  }

  const isDirty = !isEqual(initProductsTmp, productFormTmp);

  return isDirty;
}

export function productFormIsFormValid(formState, productForm) {
  const soldAlone =
    productForm?.soldAloneField === 1 || productForm?.soldAloneField === 0 || productForm?.soldAloneField === 2;
  const name = productForm?.nameProductField !== '';
  const price = productForm?.priceProductField !== '';
  const sku = productForm?.skuFieldMultiStore !== '';
  const serving = productForm?.soldAloneField !== 2 ? true : productForm?.serving && productForm?.servingUnit;

  return formState?.isValid && soldAlone && name && price && sku && serving;
}

/**
 * Checks if product form is dirty in Store mode (only editable fields)
 * In Store mode, only SAT fields, tax configuration, and kitchen areas can be modified
 *
 * @param {Object} initProduct - Initial product data
 * @param {Object} productForm - Current form data
 * @returns {boolean} Whether the form has changes in store-editable fields
 */
export function isStoreProductFormDirty(initProduct, productForm) {
  // Store mode editable fields: SAT fields, tax configuration, kitchen areas
  const storeEditableFields = {
    taxServingUnitField: productForm?.taxServingUnitField,
    taxProductKeyField: productForm?.taxProductKeyField,
    exemptField: productForm?.exemptField,
    taxGroupFieldIVA: productForm?.taxGroupFieldIVA,
    taxGroupFieldIEPS: productForm?.taxGroupFieldIEPS,
    kitchenAreasFields: productForm?.kitchenAreasFields?.sort(),
    canSelectOtherAreaField: productForm?.canSelectOtherAreaField,
    kitchenAreasFieldsCanPrint: productForm?.kitchenAreasFieldsCanPrint,
  };

  const initStoreEditableFields = {
    taxServingUnitField: initProduct?.taxServingUnitField,
    taxProductKeyField: initProduct?.taxProductKeyField,
    exemptField: initProduct?.exemptField,
    taxGroupFieldIVA: initProduct?.taxGroupFieldIVA,
    taxGroupFieldIEPS: initProduct?.taxGroupFieldIEPS,
    kitchenAreasFields: initProduct?.kitchenAreasFields?.sort(),
    canSelectOtherAreaField: initProduct?.canSelectOtherAreaField,
    kitchenAreasFieldsCanPrint: initProduct?.kitchenAreasFieldsCanPrint,
  };

  // If product is tax exempt, ignore tax group fields
  if (storeEditableFields.exemptField) {
    delete storeEditableFields.taxGroupFieldIVA;
    delete storeEditableFields.taxGroupFieldIEPS;
    delete initStoreEditableFields.taxGroupFieldIVA;
    delete initStoreEditableFields.taxGroupFieldIEPS;
  }

  return !isEqual(initStoreEditableFields, storeEditableFields);
}

/**
 * Validates if product form is valid in Store mode
 * In Store mode, validation is minimal since most fields are disabled
 * Store users should be able to save changes to SAT fields, taxes, and kitchen areas
 *
 * @param {Object} productForm - Current form data
 * @returns {boolean} Whether the form is valid for store mode
 */
export function isStoreProductFormValid(productForm) {
  // Basic validation for Store mode - ensure tax configuration is consistent
  if (!productForm?.exemptField) {
    // If not tax exempt, should have at least one tax group configured
    const hasValidTaxConfiguration = !!(
      (productForm?.taxGroupFieldIVA && productForm?.taxGroupFieldIVA !== '') ||
      (productForm?.taxGroupFieldIEPS &&
        productForm?.taxGroupFieldIEPS !== '' &&
        productForm?.taxGroupFieldIEPS !== 'noApply')
    );

    return hasValidTaxConfiguration;
  }

  // Valid if tax exempt (no tax configuration required)
  return true;
}

export const typesAddingProducts = { EXISTING: 'existing', NEW: 'new' };

export const productOverrideFormIsDirty = (initProduct, productForm, isSoldByWeight) => {
  const initProductTmp = {
    ...initProduct,
    modifierGroupsField: isSoldByWeight ? undefined : initProduct?.modifierGroupsField?.map((item) => item?.uuid),
  };

  const productFormTmp = {
    ...productForm,
    priceOverrideField: formatAmount(productForm.priceOverrideField),
    modifierGroupsField: isSoldByWeight ? undefined : productForm?.modifierGroupsField?.map((item) => item?.uuid),
  };

  return !isEqual(initProductTmp, productFormTmp);
};

export const getBaseItemsOverride = (itemsBase) => {
  let base = [];
  base = itemsBase?.map((item) => item?.modifierGroup);
  return base;
};

export const getNameProductDuplicateBarcode = (message) => {
  const tmpArray = message.split(':');
  const name = tmpArray[1].substring(0, tmpArray[1].length - 38);

  return name.trim();
};
